import {
  Heart, Truck, Activity, Stethoscope, GraduationCap, Briefcase,
} from "lucide-react";

// Stock imagery — Pexels CDN, stable across environments
export const IMAGES = {
  careersHero: "https://images.pexels.com/photos/8949908/pexels-photo-8949908.jpeg",
};

export const COMPANY = {
  name: "1st Touch Enterprise Holdings, LLC",
  shortName: "1st Touch Enterprise",
  abbr: "1TEH",
  tagline: "Empower · Invest · Innovate · Impact",
  promise: "Building Strength. Creating Opportunity. Changing Lives.",
  email: "info@1sttouchenterprise.com",
  phone: "(770) 706-2643",
  phoneRaw: "+17707062643",
  fax: "(877) 681-0814",
  address: "1820 Highway 20 SE, Suite 114 · Georgia",
  addressLine1: "1820 Highway 20 SE, Suite 114",
  addressLine2: "Georgia, USA",
  founder: "Tymeka D. Fredricks",
  founderTitle: "Founder & Chief Executive Officer",
  certifications: [
    "Certified Woman-Owned Business",
    "Certified Minority-Owned Business",
    "Pursuing Federal & State Certifications",
  ],
};

export const NAV_LINKS = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "News", to: "/healthcare-news" },
  { label: "Foundation", to: "/foundation" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    slug: "home-health",
    name: "Home Health Care",
    short: "Skilled, compassionate care in the comfort of home.",
    icon: Heart,
    color: "#003366",
    image: "https://images.pexels.com/photos/29372724/pexels-photo-29372724.jpeg",
    // NOTE: The sister site https://1sttouchhomecare.com is temporarily
    // routed through the primary domain while its SSL cert is provisioned.
    // Falls back to /homecare on 1sttouchenterprise.com, which serves the
    // same booking portal.
    action_url: "/homecare",
    action_label: "Book home care visit",
    description:
      "Our home health division delivers skilled nursing, personal care, and companionship services to individuals across Georgia, allowing patients to recover, age, and thrive in the dignity of their own homes.",
    capabilities: [
      "Skilled nursing visits",
      "Personal care assistants",
      "Medication management",
      "Care plan coordination",
      "Chronic disease support",
      "Family caregiver respite",
    ],
  },
  {
    slug: "nemt",
    name: "Non-Emergency Medical Transportation",
    short: "Safe, reliable rides to every appointment that matters.",
    icon: Truck,
    color: "#0E7A3D",
    image: "https://images.pexels.com/photos/35831411/pexels-photo-35831411.jpeg",
    external_url: "https://1sttouchmedicaltransport.com",
    description:
      "1TEH connects members to essential medical appointments, dialysis, therapy, and pharmacy visits with safety, dignity, and on-time reliability.",
    capabilities: [
      "Ambulatory & wheelchair transport",
      "Medicaid & Medicare partnerships",
      "Private-pay booking online",
      "GPS-tracked dispatch",
      "Trained, credentialed drivers",
      "ADA-compliant vehicles",
    ],
  },
  {
    slug: "therapy",
    name: "Integrated In-Home Therapy",
    short: "Physical, occupational, and speech therapy delivered at home.",
    icon: Activity,
    color: "#003366",
    image: "https://images.pexels.com/photos/5206930/pexels-photo-5206930.jpeg",
    external_url: "https://1sttouchintergratedtherapy.com",
    description:
      "Integrated therapy services combine physical, occupational, and speech-language pathology under one coordinated plan of care — improving recovery outcomes while reducing hospital readmissions.",
    capabilities: [
      "Physical therapy (PT)",
      "Occupational therapy (OT)",
      "Speech-language pathology",
      "Post-surgical rehabilitation",
      "Fall prevention programs",
      "Pediatric & geriatric care",
    ],
  },
  {
    slug: "dme",
    name: "Durable Medical Equipment",
    short: "Equipment delivered, set up, and supported at home.",
    icon: Stethoscope,
    color: "#0E7A3D",
    image: "https://images.pexels.com/photos/6129502/pexels-photo-6129502.jpeg",
    description:
      "Our DME division provides the medical equipment and supplies that make independence possible — from mobility devices to respiratory equipment — with delivery, setup, and clinician-led education.",
    capabilities: [
      "Mobility aids & wheelchairs",
      "Hospital-grade beds",
      "Oxygen & respiratory",
      "Wound care supplies",
      "In-home delivery & setup",
      "Insurance billing support",
    ],
  },
  {
    slug: "academy",
    name: "Healthcare Workforce Academy",
    short: "Training the next generation of frontline caregivers.",
    icon: GraduationCap,
    color: "#003366",
    image: "https://images.pexels.com/photos/8949908/pexels-photo-8949908.jpeg",
    description:
      "The 1TEH Academy develops the future healthcare workforce through CNA, PCA, CPR, and continuing-education programs — creating pathways into stable, meaningful healthcare careers.",
    capabilities: [
      "CNA & PCA certification",
      "CPR & First Aid training",
      "HIPAA compliance courses",
      "Career placement support",
      "Community workforce grants",
      "Continuing education credits",
    ],
  },
  {
    slug: "consulting",
    name: "Healthcare Management & Consulting",
    short: "Strategy, compliance, and operations for healthcare organizations.",
    icon: Briefcase,
    color: "#0E7A3D",
    image: "https://images.pexels.com/photos/6129502/pexels-photo-6129502.jpeg",
    description:
      "Two decades of executive healthcare expertise distilled into advisory services for agencies, MCOs, and provider groups — helping clients scale operations, win contracts, and stay compliant.",
    capabilities: [
      "Contract & RFP strategy",
      "Compliance & accreditation",
      "Operations optimization",
      "Workforce development",
      "Revenue cycle consulting",
      "Strategic partnerships",
    ],
  },
];

export const CORE_VALUES = [
  { title: "Compassion", body: "We meet every patient, family, and teammate with empathy and respect." },
  { title: "Integrity", body: "We hold ourselves to the highest ethical and clinical standards." },
  { title: "Excellence", body: "We pursue measurable quality in every service we deliver." },
  { title: "Accountability", body: "We own outcomes — to our patients, partners, and communities." },
  { title: "Innovation", body: "We embrace technology and new models that improve access." },
  { title: "Collaboration", body: "We build with — not around — caregivers, providers, and payers." },
  { title: "Diversity & Inclusion", body: "We reflect, hire, and serve the communities we work in." },
  { title: "Community Impact", body: "We measure success in lives changed, not just contracts signed." },
];

export const STATS = [
  { value: "20+", label: "Years of healthcare leadership" },
  { value: "6", label: "Integrated service divisions" },
  { value: "100%", label: "Minority & woman-owned" },
  { value: "GA", label: "Headquartered in Georgia" },
];

export const PARTNERS = [
  "Georgia Medicaid", "Medicare Advantage", "CareSource", "Amerigroup",
  "Peach State Health", "Wellcare", "Anthem BCBS",
];
